import { FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";

import { LOGIN_PATH, PASSWORD_RESET_SENT_MESSAGE } from "@buildpy/core/modules/onboarding/rules";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import SectionCard from "@buildpy/ui/section-card";
import { requestPasswordReset } from "@/api/actions/onboarding";
import { BrandLogo } from "@buildpy/ui/brand-logo";
import { Link } from "@/lib/nav";
import { RESEND_FAILED_MESSAGE, resendLabelOf, useResendCooldown } from "@/modules/onboarding/ui/signup-sent-state";
import { FORGOT_FAILED_MESSAGE } from "./forgot-password-state";

const LINK = "font-medium text-primary underline underline-offset-4 hover:text-foreground";

// 요청을 받은 화면 — 계정이 있든 없든 같은 문구 · 다시 보내기는 가입 메일과 같은 60초 대기.
export const ForgotPasswordSent = ({ email }: { email: string }) => {
  const { left, restart } = useResendCooldown();
  const [pending, startTransition] = useTransition();

  const resend = () =>
    startTransition(async () => {
      try {
        const result = await requestPasswordReset(email);
        if (result.ok) toast.success(result.message);
        else toast.error(result.message);
        restart();
      } catch {
        toast.error(RESEND_FAILED_MESSAGE);
      }
    });

  return (
    <div className="flex flex-col gap-y-4" data-testid="forgot-sent">
      <div className="flex flex-col gap-y-2">
        <h2 className="text-lg font-semibold">메일함을 확인하세요</h2>
        <p className="text-sm text-secondary-foreground">
          <span className="font-medium text-foreground" data-testid="forgot-sent-email">
            {email}
          </span>{" "}
          — {PASSWORD_RESET_SENT_MESSAGE}.
        </p>
      </div>
      <Button data-testid="forgot-resend" disabled={pending || left > 0} onClick={resend} type="button" variant="outline">
        {pending ? "보내는 중…" : resendLabelOf(left)}
      </Button>
      <p className="text-xs text-muted-foreground">메일이 오지 않으면 스팸함을 확인해 주세요. 링크는 한 번만 쓸 수 있습니다.</p>
    </div>
  );
};

const ForgotPasswordForm = ({ onSent }: { onSent: (email: string) => void }) => {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get("email") ?? "").trim();
    startTransition(async () => {
      try {
        const result = await requestPasswordReset(email);
        if (!result.ok) {
          setError(result.message);
          return;
        }
        setError(null);
        onSent(email.toLowerCase());
      } catch {
        setError(FORGOT_FAILED_MESSAGE);
      }
    });
  };

  return (
    <form className="flex flex-col gap-y-4" data-testid="forgot-form" onSubmit={onSubmit}>
      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="forgot-email">가입한 이메일</Label>
        <Input
          autoComplete="email"
          disabled={pending}
          id="forgot-email"
          inputMode="email"
          maxLength={254}
          name="email"
          placeholder="you@company.com"
          required
          type="email"
        />
      </div>
      {error && (
        <p className="text-sm text-destructive" data-testid="forgot-error" role="alert">
          {error}
        </p>
      )}
      <Button className="w-full" disabled={pending} type="submit">
        {pending ? "보내는 중…" : "재설정 메일 받기"}
      </Button>
    </form>
  );
};

export const ForgotPasswordView = ({ sent, onSent }: { sent: string | null; onSent: (email: string) => void }) => (
  <main className="flex min-h-screen items-center justify-center bg-background p-4">
    <SectionCard as="div" className="flex w-full max-w-sm flex-col gap-y-6" data-testid="forgot-page" padding="lg">
      <div className="flex flex-col gap-y-3">
        <Link aria-label="buildpy 첫 화면" className="flex" href="/">
          <BrandLogo size="lg" />
        </Link>
        {!sent && (
          <div className="flex flex-col gap-y-1">
            <h1 className="text-xl font-bold">비밀번호 찾기</h1>
            <p className="text-sm text-muted-foreground">가입한 이메일로 새 비밀번호를 정하는 링크를 보내 드립니다.</p>
          </div>
        )}
      </div>
      {sent ? <ForgotPasswordSent email={sent} /> : <ForgotPasswordForm onSent={onSent} />}
      <p className="text-sm text-muted-foreground">
        비밀번호가 생각나셨나요?{" "}
        <Link className={LINK} href={LOGIN_PATH}>
          로그인
        </Link>
      </p>
    </SectionCard>
  </main>
);

// 비밀번호 찾기 — 대표·직원 모두 · 이메일은 화면 상태에만(주소창에 싣지 않는다).
const ForgotPasswordPage = () => {
  const [sent, setSent] = useState<string | null>(null);
  return <ForgotPasswordView onSent={setSent} sent={sent} />;
};

export default ForgotPasswordPage;
